/**
 * CustomerDashboard.tsx — Customer portal dashboard
 * URL: /shop/:businessId/dashboard
 *
 * Shows the logged-in customer's orders, profile, and allows logout.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import API_URL from '../../config/api';
import {
  clearShopSession,
  isWholesaleShopCustomer,
  readShopCustomer,
  readShopToken,
  syncShopSession,
  type ShopCustomer,
} from '../../utils/shopSession';

interface Order {
  id: string;
  order_number: string;
  status: string;
  total: number;
  subtotal?: number;
  shipping_cost?: number;
  payment_method?: string;
  payment_status?: string;
  deposit_amount?: number;
  deposit_confirmed?: boolean;
  created_at: string;
  items: any[];
}

interface ShopInvoice {
  id: number;
  invoice_number: string;
  invoice_date?: string;
  due_date?: string;
  status: string;
  amount: number;
  total?: number;
  paid_amount?: number;
  created_at: string;
}

interface ShopPayment {
  id: number;
  amount: number;
  payment_date: string;
  payment_method?: string;
  notes?: string;
  invoice_id: number;
  invoice_number?: string;
  invoice_status?: string;
}

const STATUS_COLORS: Record<string, string> = {
  Draft: '#6b7280',
  Confirmed: '#3b82f6',
  Processing: '#f59e0b',
  Shipped: '#8b5cf6',
  Completed: '#10b981',
  Cancelled: '#ef4444',
  'Pending Payment': '#f59e0b',
  'Partial Payment': '#8b5cf6',
  Pending: '#6b7280',
};

const CustomerDashboard: React.FC = () => {
  const { businessId } = useParams<{ businessId: string }>();
  const navigate = useNavigate();

  const [shopToken, setShopToken] = useState<string | null>(() => (businessId ? readShopToken(businessId) : null));
  const [shopCustomer, setShopCustomer] = useState<ShopCustomer | null>(() => (businessId ? readShopCustomer(businessId) : null));

  const [orders, setOrders] = useState<Order[]>([]);
  const [invoices, setInvoices] = useState<ShopInvoice[]>([]);
  const [payments, setPayments] = useState<ShopPayment[]>([]);
  const [profile, setProfile] = useState<any>(shopCustomer);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState<'orders' | 'invoices' | 'payments' | 'profile'>('orders');

  // Load account data (refresh session from CRM first)
  useEffect(() => {
    if (!businessId) return;
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const session = await syncShopSession(businessId);
        const token = session?.token ?? readShopToken(businessId);
        const customer = session?.customer ?? readShopCustomer(businessId);
        if (!token || !customer) {
          if (!cancelled) navigate(`/shop/${businessId}/login`);
          return;
        }
        if (!cancelled) {
          setShopToken(token);
          setShopCustomer(customer);
        }

        const authHeaders = { Authorization: `Bearer ${token}` };

        const [profileData, ordersData, invoicesData, paymentsData] = await Promise.all([
          fetch(`${API_URL}/shop-auth/me`, { headers: authHeaders }).then(r => r.ok ? r.json() : null),
          fetch(`${API_URL}/shop-auth/orders/${businessId}`, { headers: authHeaders }).then(async r => {
            const data = await r.json();
            if (!r.ok) throw new Error(data.error || 'Could not load orders');
            return Array.isArray(data) ? data : [];
          }),
          fetch(`${API_URL}/shop-auth/invoices/${businessId}`, { headers: authHeaders }).then(async r => {
            const data = await r.json();
            if (!r.ok) return [];
            return Array.isArray(data) ? data : [];
          }),
          fetch(`${API_URL}/shop-auth/payments/${businessId}`, { headers: authHeaders }).then(async r => {
            const data = await r.json();
            if (!r.ok) return [];
            return Array.isArray(data) ? data : [];
          }),
        ]);

        if (cancelled) return;
        const profileCustomer = profileData?.customer || profileData;
        if (profileData?.token) {
          localStorage.setItem(`shopToken_${businessId}`, profileData.token);
        }
        if (profileCustomer) {
          setProfile(profileCustomer);
          setShopCustomer(profileCustomer);
        }
        setOrders(ordersData);
        setInvoices(invoicesData);
        setPayments(paymentsData);
      } catch (err: any) {
        if (!cancelled) setError(err.message || 'Could not load account data.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [businessId, navigate]);

  const handleLogout = () => {
    clearShopSession(businessId!);
    navigate(`/shop/${businessId}`);
  };

  const handleReorder = (order: Order) => {
    const items: any[] = Array.isArray(order.items) ? order.items : [];
    if (items.length === 0) { alert('No items found in this order.'); return; }
    const cartItems = items.map((item: any) => ({
      product: {
        id: String(item.productId || item.product_id || ''),
        name: item.productName || item.product_name || item.name || 'Product',
        price: Number(item.unitPrice || item.unit_price || item.price || 0),
        wholesale_price: Number(item.unitPrice || item.unit_price || item.price || 0),
        stock: 999,
        image: '', image1: '', image2: '', image3: '',
        sku: item.sku || '',
        category: '',
        unit: item.unit || 'pcs',
        description: item.description || '',
        box_quantity: item.box_quantity || null,
        tax_rate: 20,
      },
      qty: Number(item.qty || item.quantity || 1),
      saleType: item.saleType || item.sale_type || 'unit',
    }));
    if (businessId) localStorage.setItem(`cart_${businessId}`, JSON.stringify(cartItems));
    navigate(`/shop/${businessId}`);
  };

  if (!shopCustomer) return null;

  const displayCustomer = profile || shopCustomer;
  const isWholesale = isWholesaleShopCustomer(displayCustomer.customer_type);

  return (
    <div style={{ minHeight: '100vh', background: '#f9fafb', fontFamily: 'system-ui, sans-serif' }}>
      {/* Header */}
      <header style={{ background: '#1e3a8a', color: '#fff', padding: '14px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.2)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Link to={`/shop/${businessId}`} style={{ color: '#fff', textDecoration: 'none', fontSize: 13, opacity: 0.75 }}>← Shop</Link>
          <span style={{ opacity: 0.4 }}>|</span>
          <span style={{ fontSize: 16, fontWeight: 800 }}>My Account</span>
          {isWholesale && <span style={{ fontSize: 11, background: '#3b82f6', padding: '2px 10px', borderRadius: 10, fontWeight: 700 }}>📦 Wholesale</span>}
        </div>
        <button
          onClick={handleLogout}
          style={{ padding: '6px 14px', borderRadius: 7, border: 'none', background: 'rgba(255,50,50,0.25)', color: '#fff', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}
        >
          Logout
        </button>
      </header>

      <div style={{ maxWidth: 800, margin: '0 auto', padding: '28px 20px 60px' }}>
        {/* Welcome card */}
        <div style={{ background: '#fff', borderRadius: 12, padding: '20px 24px', marginBottom: 20, boxShadow: '0 1px 4px rgba(0,0,0,0.06)', display: 'flex', alignItems: 'center', gap: 16 }}>
          <div style={{ width: 48, height: 48, borderRadius: '50%', background: '#1e3a8a', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0 }}>
            👤
          </div>
          <div>
            <div style={{ fontWeight: 800, fontSize: 17, color: '#111827' }}>Hello, {displayCustomer.name}!</div>
            <div style={{ fontSize: 13, color: '#6b7280', marginTop: 2 }}>{displayCustomer.email}</div>
          </div>
          {isWholesale && (
            <div style={{ marginLeft: 'auto', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: '6px 14px', fontSize: 13, color: '#1d4ed8', fontWeight: 700 }}>
              📦 Wholesale account<br />
              <span style={{ fontSize: 11, fontWeight: 400, color: '#6b7280' }}>Wholesale prices applied</span>
            </div>
          )}
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 4, marginBottom: 20, background: '#fff', borderRadius: 10, padding: 4, border: '1px solid #e5e7eb', width: 'fit-content' }}>
          {(['orders', 'invoices', 'payments', 'profile'] as const).map(tab => (
            <button key={tab} onClick={() => setActiveTab(tab)}
              style={{ padding: '8px 20px', borderRadius: 8, border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 13, background: activeTab === tab ? '#1e3a8a' : 'transparent', color: activeTab === tab ? '#fff' : '#6b7280' }}>
              {tab === 'orders' ? '📦 Orders' : tab === 'invoices' ? '🧾 Invoices' : tab === 'payments' ? '💳 Payments' : '👤 Profile'}
            </button>
          ))}
        </div>

        {/* Orders Tab */}
        {activeTab === 'orders' && (
          <div>
            {loading ? (
              <div style={{ textAlign: 'center', padding: '40px 0', color: '#9ca3af' }}>
                <div style={{ fontSize: 32, marginBottom: 8 }}>⏳</div>
                <p>Loading your orders…</p>
              </div>
            ) : error ? (
              <div style={{ background: '#fef2f2', borderRadius: 10, padding: 20, color: '#dc2626', textAlign: 'center' }}>⚠️ {error}</div>
            ) : orders.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '60px 0', color: '#9ca3af', background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb' }}>
                <div style={{ fontSize: 48, marginBottom: 12 }}>📦</div>
                <p style={{ fontSize: 15, fontWeight: 600 }}>No orders yet</p>
                <p style={{ fontSize: 13, marginTop: 4 }}>Start shopping and your orders will appear here.</p>
                <Link to={`/shop/${businessId}`} style={{ marginTop: 16, display: 'inline-block', padding: '10px 24px', borderRadius: 8, background: '#1e3a8a', color: '#fff', fontWeight: 700, fontSize: 14, textDecoration: 'none' }}>
                  Go to Shop
                </Link>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                {orders.map(order => {
                  const statusColor = STATUS_COLORS[order.status] || '#6b7280';
                  const date = new Date(order.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
                  const items: any[] = Array.isArray(order.items) ? order.items : [];
                  const isDeposit = (order.payment_method === 'Part Payment' || order.status === 'Partial Payment') && order.deposit_amount != null;
                  const remaining = isDeposit ? Math.max(0, Number(order.total) - Number(order.deposit_amount)) : 0;

                  // Payment method label
                  const pmLabel: Record<string, string> = {
                    'Bank Transfer': '🏦 Bank Transfer',
                    'Cash on Collection': '💵 Cash on Collection',
                    'Part Payment': '💰 Part Payment / Deposit',
                  };

                  return (
                    <div key={order.id} style={{ background: '#fff', borderRadius: 14, boxShadow: '0 1px 6px rgba(0,0,0,0.07)', border: '1px solid #e5e7eb', overflow: 'hidden' }}>
                      {/* Order header */}
                      <div style={{ padding: '14px 18px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, borderBottom: '1px solid #f3f4f6' }}>
                        <div>
                          <div style={{ fontWeight: 800, fontSize: 14, color: '#111827' }}>{order.order_number || `Order #${order.id}`}</div>
                          <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 2 }}>{date}</div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          {order.payment_status && (
                            <span style={{ fontSize: 11, color: '#374151', background: '#f3f4f6', padding: '2px 8px', borderRadius: 10 }}>
                              {order.payment_status}
                            </span>
                          )}
                          {order.payment_method && (
                            <span style={{ fontSize: 11, color: '#6b7280' }}>{pmLabel[order.payment_method] || order.payment_method}</span>
                          )}
                          <span style={{ fontSize: 12, fontWeight: 700, padding: '4px 12px', borderRadius: 20, background: `${statusColor}18`, color: statusColor, border: `1.5px solid ${statusColor}40` }}>
                            {order.status}
                          </span>
                          <button
                            onClick={() => handleReorder(order)}
                            title="Add all items from this order to your cart"
                            style={{ padding: '4px 12px', borderRadius: 8, border: '1.5px solid #3b82f6', background: '#eff6ff', color: '#1d4ed8', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}
                          >
                            🔁 Reorder
                          </button>
                        </div>
                      </div>

                      {/* Items table */}
                      {items.length > 0 && (
                        <div style={{ overflowX: 'auto' }}>
                          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                            <thead>
                              <tr style={{ background: '#f9fafb' }}>
                                <th style={{ textAlign: 'left', padding: '8px 18px', fontWeight: 600, color: '#6b7280', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Item</th>
                                <th style={{ textAlign: 'center', padding: '8px 12px', fontWeight: 600, color: '#6b7280', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Qty</th>
                                <th style={{ textAlign: 'right', padding: '8px 12px', fontWeight: 600, color: '#6b7280', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Unit Price</th>
                                <th style={{ textAlign: 'right', padding: '8px 18px', fontWeight: 600, color: '#6b7280', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Line Total</th>
                              </tr>
                            </thead>
                            <tbody>
                              {items.map((item: any, idx: number) => {
                                const qty = item.quantity || item.qty || 1;
                                const unitPrice = item.unitPrice || item.unit_price || 0;
                                const lineTotal = item.total || (qty * unitPrice);
                                return (
                                  <tr key={idx} style={{ borderTop: '1px solid #f3f4f6' }}>
                                    <td style={{ padding: '10px 18px', color: '#111827', fontWeight: 500 }}>
                                      {item.productName || item.product_name || item.name || 'Item'}
                                    </td>
                                    <td style={{ padding: '10px 12px', textAlign: 'center', color: '#374151' }}>{qty}</td>
                                    <td style={{ padding: '10px 12px', textAlign: 'right', color: '#374151' }}>£{Number(unitPrice).toFixed(2)}</td>
                                    <td style={{ padding: '10px 18px', textAlign: 'right', color: '#111827', fontWeight: 600 }}>£{Number(lineTotal).toFixed(2)}</td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}

                      {/* Totals + deposit */}
                      <div style={{ padding: '12px 18px', borderTop: '1px solid #f3f4f6', background: '#fafafa' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxWidth: 280, marginLeft: 'auto' }}>
                          {order.shipping_cost && Number(order.shipping_cost) > 0 && (
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: '#6b7280' }}>
                              <span>Shipping</span>
                              <span>£{Number(order.shipping_cost).toFixed(2)}</span>
                            </div>
                          )}
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 15, fontWeight: 800, color: '#111827', paddingTop: 4, borderTop: '1.5px solid #e5e7eb' }}>
                            <span>Order Total</span>
                            <span>£{Number(order.total).toFixed(2)}</span>
                          </div>
                          {isDeposit && (
                            <>
                              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: '#059669', fontWeight: 700, marginTop: 4 }}>
                                <span>✅ Deposit Paid</span>
                                <span>£{Number(order.deposit_amount).toFixed(2)}</span>
                              </div>
                              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: '#b45309', fontWeight: 700 }}>
                                <span>⏳ Remaining Balance</span>
                                <span>£{remaining.toFixed(2)}</span>
                              </div>
                              <div style={{ marginTop: 6, fontSize: 12, color: '#78350f', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 6, padding: '6px 10px' }}>
                                We will contact you to arrange the remaining balance of £{remaining.toFixed(2)}.
                              </div>
                            </>
                          )}
                          {order.payment_method === 'Bank Transfer' && order.status === 'Pending Payment' && (
                            <div style={{ marginTop: 6, fontSize: 12, color: '#1e3a8a', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 6, padding: '6px 10px' }}>
                              🏦 Please transfer £{Number(order.total).toFixed(2)} to complete your order. We'll confirm once payment is received.
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Invoices Tab */}
        {activeTab === 'invoices' && (
          <div>
            {loading ? (
              <div style={{ textAlign: 'center', padding: '40px 0', color: '#9ca3af' }}>⏳ Loading invoices…</div>
            ) : invoices.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '60px 0', color: '#9ca3af', background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb' }}>
                <div style={{ fontSize: 48, marginBottom: 12 }}>🧾</div>
                <p style={{ fontSize: 15, fontWeight: 600 }}>No invoices yet</p>
                <p style={{ fontSize: 13, marginTop: 4 }}>Invoices generated from your orders will appear here.</p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {invoices.map(inv => (
                  <div key={inv.id} style={{ background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', padding: '16px 18px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                      <div>
                        <div style={{ fontWeight: 800, color: '#111827' }}>{inv.invoice_number || `Invoice #${inv.id}`}</div>
                        <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 4 }}>
                          {inv.invoice_date ? new Date(inv.invoice_date).toLocaleDateString('en-GB') : new Date(inv.created_at).toLocaleDateString('en-GB')}
                        </div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontWeight: 800, fontSize: 18 }}>£{Number(inv.amount || inv.total || 0).toFixed(2)}</div>
                        <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>{inv.status}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Payments Tab */}
        {activeTab === 'payments' && (
          <div>
            {loading ? (
              <div style={{ textAlign: 'center', padding: '40px 0', color: '#9ca3af' }}>⏳ Loading payments…</div>
            ) : payments.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '60px 0', color: '#9ca3af', background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb' }}>
                <div style={{ fontSize: 48, marginBottom: 12 }}>💳</div>
                <p style={{ fontSize: 15, fontWeight: 600 }}>No payments recorded yet</p>
                <p style={{ fontSize: 13, marginTop: 4 }}>Payments against your invoices will appear here once recorded.</p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {payments.map(payment => (
                  <div key={payment.id} style={{ background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', padding: '16px 18px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                      <div>
                        <div style={{ fontWeight: 700, color: '#111827' }}>
                          £{Number(payment.amount).toFixed(2)} — {payment.payment_method || 'Payment'}
                        </div>
                        <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 4 }}>
                          {payment.payment_date ? new Date(payment.payment_date).toLocaleDateString('en-GB') : '—'}
                          {payment.invoice_number ? ` · Invoice ${payment.invoice_number}` : payment.invoice_id ? ` · Invoice #${payment.invoice_id}` : ''}
                        </div>
                        {payment.notes && <div style={{ fontSize: 12, color: '#6b7280', marginTop: 6 }}>{payment.notes}</div>}
                      </div>
                      <div style={{ fontSize: 12, color: '#6b7280', alignSelf: 'center' }}>{payment.invoice_status || ''}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Profile Tab */}
        {activeTab === 'profile' && (
          <div style={{ background: '#fff', borderRadius: 12, padding: '24px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', border: '1px solid #e5e7eb' }}>
            <h2 style={{ margin: '0 0 20px', fontSize: 16, fontWeight: 700, color: '#111827' }}>👤 Your Profile</h2>
            <div style={{ display: 'grid', gap: 14 }}>
              <Row label="Name" value={displayCustomer.name} />
              <Row label="Email" value={displayCustomer.email} />
              {displayCustomer.phone && <Row label="Phone" value={displayCustomer.phone} />}
              {[displayCustomer.address, displayCustomer.city, displayCustomer.postcode, displayCustomer.country].filter(Boolean).length > 0 && (
                <Row label="Address" value={[displayCustomer.address, displayCustomer.city, displayCustomer.postcode, displayCustomer.country].filter(Boolean).join(', ')} />
              )}
              <Row label="Account type" value={isWholesale ? '📦 Wholesale' : '🛒 Retail'} />
            </div>
            <div style={{ marginTop: 20, padding: '12px 14px', background: '#fffbeb', borderRadius: 8, border: '1px solid #fde68a', fontSize: 12, color: '#92400e' }}>
              💡 To update your profile details or upgrade to wholesale, please contact the shop.
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const Row: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid #f3f4f6', fontSize: 14 }}>
    <span style={{ color: '#6b7280', fontWeight: 600 }}>{label}</span>
    <span style={{ color: '#111827', fontWeight: 500 }}>{value}</span>
  </div>
);

export default CustomerDashboard;
